import { Link } from 'react-router-dom';
import { ChildRecords, lineNumber, type FieldSpec, type FormValues } from '@/components/ChildRecords';
import { QueryState, StatusPill, formatDate, formatNumber, todayIso } from '@/components/live';
import { useRows } from '@/hooks/useResource';
import { logVolume } from '@/screens/parts/harvestMaths';

/**
 * The tabs of a harvest (owner, 2026-09-27, the harvest package): its photographs, the trucks that
 * carried it, and the saplings that restock it. The crew's pay is ActivityLabour, as silviculture's.
 */

export const PHOTO_KINDS = [
  { id: 'harvested_site', label: 'Harvested site' },
  { id: 'logs_stacked', label: 'Logs stacked' },
  { id: 'other', label: 'Other' },
];

interface Photo {
  id: string; photo_code: string | null; photo_kind: string; taken_on: string; document_id: string;
  latitude: number | null; longitude: number | null; remarks: string | null;
}

/** Two of the felled site and two of the stacked logs make the report complete (owner: "at-least 2 photographs"). */
export function HarvestPhotos({ harvest }: { harvest: { id: string; harvest_date?: string | null; felling_completed_on?: string | null } }) {
  const photos = useRows<Photo>('harvest_photos', { filters: { harvest_id: harvest.id } });
  const count = (kind: string) => (photos.data ?? []).filter((row) => row.photo_kind === kind).length;
  const site = count('harvested_site');
  const logs = count('logs_stacked');
  return (
    <ChildRecords<Photo>
      addLabel="Add photo" emptyText="No photograph yet. The report needs two of the harvested site and two of the stacked logs."
      defaults={{ photo_kind: site < 2 ? 'harvested_site' : 'logs_stacked', taken_on: harvest.felling_completed_on ?? harvest.harvest_date ?? todayIso() }}
      fields={[
        { key: 'photo_kind', label: 'Of', type: 'select', options: PHOTO_KINDS, required: true },
        { key: 'taken_on', label: 'Taken on', type: 'date', required: true, max: todayIso() },
        { key: 'document_id', label: 'Photo', type: 'file', accept: 'image/*', required: true },
        { key: 'latitude', lngKey: 'longitude', label: 'Where (GPS)', type: 'gps' },
        { key: 'remarks', label: 'Remarks', type: 'text' },
      ]}
      filters={{ harvest_id: harvest.id }} invalidate={['plot_harvest_entries']} intro={`Harvested site ${site} of 2 · logs stacked ${logs} of 2${site >= 2 && logs >= 2 ? ' - complete.' : '.'}`}
      orderBy={{ column: 'taken_on', ascending: false }} ownerId={harvest.id} parent={{ harvest_id: harvest.id }}
      screenKey="plot.harvest" table="ops.harvest_photos"
      summary={(row) => ({
        title: `${PHOTO_KINDS.find((kind) => kind.id === row.photo_kind)?.label ?? row.photo_kind} - ${formatDate(row.taken_on)}`,
        detail: [row.photo_code, row.latitude != null && row.longitude != null ? `${formatNumber(row.latitude, 5)}, ${formatNumber(row.longitude, 5)}` : null, row.remarks]
          .filter(Boolean).join(' · '),
      })}
      title="Photographs" view="harvest_photos"
    />
  );
}

interface Docket {
  id: string; docket_code: string | null; trade_code: string | null; dispatched_on: string; vehicle_no: string | null; driver_name: string | null;
  quantity: number; quantity_unit_label: string | null; net_weight_t: number | null; eway_bill_no: string | null; papers_missing: string[] | null;
}

/** Every truck that carried this harvest away: the dockets of the sales made from it (owner: "multiple truck transportation"). */
export function HarvestTrucks({ harvestId }: { harvestId: string }) {
  const dockets = useRows<Docket>('sale_dockets', { filters: { harvest_id: harvestId }, orderBy: { column: 'dispatched_on', ascending: true } });
  const rows = dockets.data ?? [];
  const short = rows.filter((row) => (row.papers_missing ?? []).length > 0).length;
  return (
    <section className="child-records">
      <div className="section-heading">
        <h3>Trucks <span className="muted">({rows.length})</span></h3>
        <Link className="button-link" to="/sale-dockets">Add a load on Sale dockets</Link>
      </div>
      <p className="muted child-records-intro">
        One docket per truck, under the sale it carried. Each needs two loaded photos with the number plate, the driver&apos;s licence,
        RC, insurance, PUC, the transit permit and the weighment slip{short ? ` - ${short} still short of papers.` : '.'}
      </p>
      <QueryState empty="No load has left under a sale of this harvest yet." query={dockets}>
        <ul className="child-record-list">
          {rows.map((row) => (
            <li key={row.id}>
              <span className="child-record-text">
                <strong>{row.vehicle_no ?? 'Vehicle not recorded'} - {formatDate(row.dispatched_on)}{' '}
                  <StatusPill value={(row.papers_missing ?? []).length ? 'papers missing' : 'papers complete'} /></strong>
                <small className="muted">{[row.docket_code, row.trade_code, row.driver_name,
                  `${formatNumber(row.quantity, 2)} ${row.quantity_unit_label ?? ''}`,
                  row.net_weight_t != null ? `${formatNumber(row.net_weight_t, 3)} t net` : null,
                  row.eway_bill_no ? `e-way ${row.eway_bill_no}` : null,
                  (row.papers_missing ?? []).length ? `missing: ${(row.papers_missing ?? []).join(', ')}` : null].filter(Boolean).join(' · ')}</small>
              </span>
            </li>
          ))}
        </ul>
      </QueryState>
    </section>
  );
}

interface Issue { id: string; sapling_issue_code: string | null; issued_on: string; quantity_issued: number; species_label: string | null; nursery_name: string | null }

/** The saplings handed over to restock this harvest, against the plan (owner: "regeneration ... seedling distribution"). */
export function HarvestRestocking({ harvest }: {
  harvest: { id: string; regeneration_method: string | null; regeneration_saplings_planned: number | null; regeneration_due_on: string | null;
             regeneration_state: string };
}) {
  const issues = useRows<Issue>('sapling_issues', { filters: { harvest_id: harvest.id }, orderBy: { column: 'issued_on', ascending: true } });
  const rows = issues.data ?? [];
  const issued = rows.reduce((sum, row) => sum + Number(row.quantity_issued || 0), 0);
  return (
    <section className="child-records">
      <div className="section-heading">
        <h3>Restocking <span className="muted">({rows.length})</span></h3>
        <Link className="button-link" to="/sapling-issues">Record an issue on Sapling issue</Link>
      </div>
      <dl className="detail-grid">
        <div><dt>How it regenerates</dt><dd>{REGENERATION_METHODS.find((entry) => entry.id === harvest.regeneration_method)?.label ?? 'Not set on Details'}</dd></div>
        <div><dt>Saplings planned</dt><dd>{harvest.regeneration_saplings_planned != null ? formatNumber(harvest.regeneration_saplings_planned, 0) : '--'}</dd></div>
        <div><dt>Saplings issued</dt><dd>{formatNumber(issued, 0)}</dd></div>
        <div><dt>By</dt><dd>{harvest.regeneration_due_on ? formatDate(harvest.regeneration_due_on) : '--'}</dd></div>
        <div><dt>State</dt><dd><StatusPill value={REGENERATION_STATES[harvest.regeneration_state] ?? harvest.regeneration_state} /></dd></div>
      </dl>
      <QueryState empty="No sapling issue names this harvest yet. Record one on Sapling issue and choose this harvest under 'Restocks harvest'." query={issues}>
        <ul className="child-record-list">
          {rows.map((row) => (
            <li key={row.id}>
              <span className="child-record-text">
                <strong>{formatNumber(row.quantity_issued, 0)} {row.species_label ?? 'saplings'} - {formatDate(row.issued_on)}</strong>
                <small className="muted">{[row.sapling_issue_code, row.nursery_name].filter(Boolean).join(' · ')}</small>
              </span>
            </li>
          ))}
        </ul>
      </QueryState>
    </section>
  );
}

export const REGENERATION_METHODS = [
  { id: 'replanting', label: 'Replanting (saplings)' },
  { id: 'assisted_natural', label: 'Assisted natural regeneration (gap planting)' },
  { id: 'natural', label: 'Natural regeneration' },
  { id: 'coppice', label: 'Coppice regrowth' },
  { id: 'not_required', label: 'Not required (land use continues)' },
];

export const REGENERATION_STATES: Record<string, string> = {
  not_set: 'Not planned', no_stock_needed: 'No stock needed', complete: 'Restocked', partial: 'Partly restocked',
  overdue: 'Restocking overdue', pending: 'Restocking due',
};

export const YIELD_BASES = [
  { id: 'log_volume', label: 'Log by log (m³)' },
  { id: 'weight', label: 'By weight (tonnes)' },
];

export const YIELD_FORMULAS = [
  { id: 'hoppus', label: 'Quarter-girth (Hoppus): (g/4)² × L' },
  { id: 'true_mid_girth', label: 'True volume: g² × L / 4π' },
];


const isLog = (form: FormValues) => form.yield_basis === 'log_volume';
const isWeight = (form: FormValues) => form.yield_basis === 'weight';

/**
 * The yield statement's fields (owner, 2026-09-27: "some are based on tonnage, some are based on M3 -
 * log wise"): a log per line with its length and mid-girth, or a load per line with its weights.
 * The volume and the net weight shown are recomputed by the database when saved.
 */
export function yieldFields(section = 'Yield'): FieldSpec[] {
  return [
    { key: 'yield_basis', label: 'Measured', type: 'select', options: YIELD_BASES, section,
      hint: 'Timber logs are usually measured log by log in m³; pulpwood, poles and fuelwood by weight on a weighbridge.' },
    { key: 'yield_formula', label: 'Volume formula', type: 'select', options: YIELD_FORMULAS, showIf: isLog, required: true },
    { key: 'yield_lines', label: 'Logs / loads', type: 'lines', linesInColumn: true, addLineLabel: 'Add a line',
      showIf: (form) => isLog(form) || isWeight(form),
      hintFor: (form) => (isLog(form)
        ? 'One line per log, or per group of logs of the same size with the number of pieces. Girth at the middle, over bark, in cm.'
        : 'One line per weighbridge ticket: the loaded (gross) and empty (tare) weight of the truck.'),
      columns: [
        { key: 'log_no', label: 'Log no.', type: 'text', showIf: isLog, minWidth: 90 },
        { key: 'length_m', label: 'Length (m)', type: 'number', required: true, showIf: isLog },
        { key: 'girth_cm', label: 'Mid-girth (cm)', type: 'number', required: true, showIf: isLog },
        // Blank means one log, as the database counts it; shown greyed and counted in the total.
        { key: 'pieces', label: 'Pieces', type: 'number', showIf: isLog, total: true,
          compute: (line) => (lineNumber(line.length_m) != null || lineNumber(line.girth_cm) != null ? '1' : '') },
        { key: 'volume_m3', label: 'Volume (m³)', type: 'computed', showIf: isLog, total: true,
          compute: (line, form) => {
            const volume = logVolume(lineNumber(line.length_m), lineNumber(line.girth_cm), lineNumber(line.pieces), String(form.yield_formula ?? 'hoppus'));
            return volume == null ? '' : volume.toFixed(4);
          } },
        { key: 'ticket_no', label: 'Weighbridge ticket', type: 'text', showIf: isWeight, minWidth: 120 },
        { key: 'vehicle_no', label: 'Vehicle', type: 'text', showIf: isWeight, minWidth: 110 },
        { key: 'gross_t', label: 'Gross (t)', type: 'number', required: true, showIf: isWeight, total: true },
        { key: 'tare_t', label: 'Tare (t)', type: 'number', showIf: isWeight, total: true },
        { key: 'net_t', label: 'Net (t)', type: 'computed', showIf: isWeight, total: true,
          compute: (line) => {
            const gross = lineNumber(line.gross_t);
            return gross == null ? '' : (gross - (lineNumber(line.tare_t) ?? 0)).toFixed(3);
          } },
      ] },
  ];
}

/** How the site comes back (owner: "regeneration aspect associated for seedling distribution"). */
export function regenerationFields(section = 'Regeneration'): FieldSpec[] {
  const needsStock = (form: FormValues) => form.regeneration_method === 'replanting' || form.regeneration_method === 'assisted_natural';
  return [
    { key: 'regeneration_method', label: 'How the site regenerates', type: 'select', options: REGENERATION_METHODS, section },
    { key: 'regeneration_saplings_planned', label: 'Saplings needed', type: 'number', required: true, showIf: needsStock,
      hint: 'Issue them on Sapling issue, naming this harvest; the Restocking tab counts them.' },
    { key: 'regeneration_due_on', label: 'Restock by', type: 'date', showIf: (form) => Boolean(form.regeneration_method) && form.regeneration_method !== 'not_required' },
    { key: 'regeneration_remarks', label: 'Post-harvest measures', type: 'textarea',
      placeholder: 'e.g. coppice shoots singled in year 1; gaps planted after the monsoon; site closed to grazing' },
  ];
}
